import React, { useState } from 'react';
import { FiBarChart2, FiUpload, FiFile, FiTrendingUp, FiPieChart } from 'react-icons/fi';

const CourtageDetail = ({ clientData }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analysisStatus, setAnalysisStatus] = useState('ready');

  const handleFileUpload = (files) => {
    const newFiles = files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: file.size,
      status: 'analyzing',
      confidence: 0,
      uploadDate: new Date().toLocaleString('fr-FR')
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Simulation analyse
    newFiles.forEach((file, index) => {
      setTimeout(() => {
        setUploadedFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', confidence: 85 + Math.random() * 15 }
            : f
        ));
      }, 2000 + index * 500);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
        <div className="flex items-center space-x-3">
          <FiBarChart2 className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-bold">Analyse Courtage</h1>
            <p className="text-blue-100">Comparaison des offres fournisseurs pour {clientData.name}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        
        {/* Zone d'upload - 5 colonnes */}
        <div className="col-span-5 space-y-6">
          
          {/* Upload principal */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Upload des Offres</h2>
            </div>
            <div className="p-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <FiUpload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Déposez vos offres fournisseurs
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Formats acceptés: PDF, DOC, DOCX
                </p>
                <input 
                  type="file" 
                  multiple 
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                  id="courtage-upload"
                />
                <label 
                  htmlFor="courtage-upload"
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                >
                  <FiFile className="h-4 w-4" />
                  <span>Sélectionner fichiers</span>
                </label>
              </div>
            </div>
          </div>

          {/* Liste des fichiers */}
          {uploadedFiles.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Documents Analysés</h2>
              </div>
              <div className="p-4 space-y-3">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FiFile className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {file.confidence > 0 ? `Confiance: ${file.confidence.toFixed(0)}%` : 'En cours...'}
                        </p>
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${
                      file.status === 'completed' ? 'bg-green-500' : 
                      file.status === 'analyzing' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'
                    }`}></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bouton analyse */}
          <button 
            onClick={() => setAnalysisStatus('completed')}
            disabled={uploadedFiles.length === 0}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <FiBarChart2 className="h-4 w-4" />
            <span>Lancer l'analyse comparative</span>
          </button>
        </div>

        {/* Résultats - 7 colonnes */}
        <div className="col-span-7 space-y-6">
          
          {analysisStatus === 'completed' ? (
            <>
              {/* Tableau comparatif */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h2 className="font-semibold text-gray-900">Comparaison des Offres</h2>
                </div>
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 text-gray-600 font-medium">Fournisseur</th>
                          <th className="text-right py-3 text-gray-600 font-medium">Prix kWh</th>
                          <th className="text-right py-3 text-gray-600 font-medium">Abonnement</th>
                          <th className="text-right py-3 text-gray-600 font-medium">Total Annuel</th>
                          <th className="text-right py-3 text-gray-600 font-medium">Économie</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className="py-3 font-medium">EDF</td>
                          <td className="text-right py-3">0.1567 €</td>
                          <td className="text-right py-3">156.24 €</td>
                          <td className="text-right py-3">8 945 €</td>
                          <td className="text-right py-3 text-red-600">-</td>
                        </tr>
                        <tr>
                          <td className="py-3 font-medium">Engie</td>
                          <td className="text-right py-3">0.1489 €</td>
                          <td className="text-right py-3">142.36 €</td>
                          <td className="text-right py-3">8 234 €</td>
                          <td className="text-right py-3 text-green-600">+711 €</td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="py-3 font-medium">
                            <div className="flex items-center space-x-2">
                              <span>TotalEnergies</span>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                Recommandé
                              </span>
                            </div>
                          </td>
                          <td className="text-right py-3">0.1445 €</td>
                          <td className="text-right py-3">136.90 €</td>
                          <td className="text-right py-3 font-semibold">7 892 €</td>
                          <td className="text-right py-3 text-green-600 font-semibold">+1 053 €</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Graphiques */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Répartition des Coûts</h3>
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <FiPieChart className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Graphique en camembert</p>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Évolution des Prix</h3>
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <FiTrendingUp className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Graphique linéaire</p>
                  </div>
                </div>
              </div>

              {/* Recommandation détaillée */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-3">💡 Recommandation IA</h3>
                <p className="text-green-800 mb-4">
                  Après analyse des 3 offres, <strong>TotalEnergies Pro</strong> présente le meilleur rapport qualité-prix 
                  avec une économie de <strong>1 053€/an</strong> (12% d'économie).
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-green-700">📊 Avantages:</span>
                    <ul className="mt-1 text-green-800 list-disc list-inside">
                      <li>Prix kWh le plus bas</li>
                      <li>Abonnement avantageux</li>
                      <li>Service client premium</li>
                    </ul>
                  </div>
                  <div>
                    <span className="text-green-700">⚡ Impact:</span>
                    <ul className="mt-1 text-green-800 list-disc list-inside">
                      <li>Économie annuelle: 1 053€</li>
                      <li>ROI immédiat</li>
                      <li>Contrat 3 ans</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <FiBarChart2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">En attente d'analyse</h3>
              <p className="text-gray-600">
                Uploadez les offres fournisseurs et lancez l'analyse pour voir les résultats comparatifs.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourtageDetail;
